/**
 * محرك الصلاحيات ونطاقات الوصول.
 *
 * هذه الطبقة نقية تمامًا (pure) ولا تعرف شيئًا عن قاعدة البيانات أو الواجهة،
 * لذلك يمكن اختبارها بسهولة، وتُستخدم نفسها في:
 *   - الواجهة (لإظهار/إخفاء العناصر)،
 *   - الخدمات قبل تنفيذ أي عملية حساسة،
 *   - طبقة البيانات المحلية (لمطابقة سلوك RLS دون اتصال).
 *
 * تحذير: النتيجة هنا ليست بديلًا عن RLS. في وضع Supabase تبقى قاعدة البيانات
 * هي المرجع النهائي، وهذه الطبقة تُستخدم للتحقق المبكر ولتجربة الاستخدام.
 */

import type {
  Membership,
  MembershipAccessGrant,
  MembershipStatus,
  Role,
  RolePermission,
  RoleScope,
  ScopeType,
  Task,
} from './types';
import type { TimeZone, UUID } from './ids';
import { COMPANY_PERMISSIONS, isPermissionKey, PLATFORM_PERMISSIONS, type PermissionKey } from './permissions';
import type { AssigneeScopeSnapshot } from './types';

export interface ScopeGrant {
  scopeType: ScopeType;
  targetId: UUID | null;
}

export interface RoleGrant {
  role: Pick<Role, 'id' | 'name' | 'key' | 'priority' | 'isPlatformRole'>;
  permissions: PermissionKey[];
  scopes: ScopeGrant[];
}

export interface MembershipContext {
  membership: Membership;
  roles: RoleGrant[];
  /** منح مباشر فعّال (revokedAt = null) */
  grants: MembershipAccessGrant[];
  /** عضويات يشرف عليها هذا المستخدم فعليًا */
  supervisedMembershipIds: UUID[];
  /** نفس العلاقات مع تفاصيل القدرات */
  supervisionCapabilities: Record<UUID, SupervisionCapability>;
}

export interface SupervisionCapability {
  canViewTasks: boolean;
  canAssignTasks: boolean;
  canApproveTasks: boolean;
  canViewReports: boolean;
}

export interface AccessContext {
  userId: UUID;
  isPlatformOwner: boolean;
  /** صلاحيات تشغيل المنصة المفوضة لهذا الحساب (لا تتضمن ملكية المنصة). */
  platformPermissions?: PermissionKey[];
  /** سياق الشركة النشطة، أو null إذا لم تكن هناك عضوية نشطة */
  membership: MembershipContext | null;
  /** كل عضويات المستخدم الفعّالة عبر الشركات (لاختيار سياق العمل) */
  memberships: Membership[];
}

export const ACTIVE_MEMBERSHIP_STATUSES: MembershipStatus[] = ['active'];

export function isMembershipActive(m: Pick<Membership, 'status'>): boolean {
  return ACTIVE_MEMBERSHIP_STATUSES.includes(m.status);
}

/**
 * دمج الصلاحيات من كل الأدوار الفعّالة.
 * الأدوار لا تُلغي بعضها: الاتحاد هو القاعدة، والقيود تُطبَّق عبر النطاق.
 */
export function effectivePermissions(ctx: AccessContext): Set<PermissionKey> {
  const result = new Set<PermissionKey>();
  if (ctx.isPlatformOwner) {
    for (const p of PLATFORM_PERMISSIONS) result.add(p);
  }
  // مالك المنشأة ليس بالضرورة مرتبطًا بدور افتراضي؛ الملكية نفسها تمنحه
  // صلاحيات إدارة منشأته فقط، ولا تمنحه صلاحيات المنصة.
  if (ctx.membership?.membership.isCompanyOwner) {
    for (const p of COMPANY_PERMISSIONS) result.add(p);
  }
  for (const permission of ctx.platformPermissions ?? []) {
    if (PLATFORM_PERMISSIONS.includes(permission)) result.add(permission);
  }
  const roles = ctx.membership?.roles ?? [];
  for (const role of roles) {
    for (const p of role.permissions) {
      if (isPermissionKey(p)) result.add(p);
    }
  }
  // منح مباشر لا يضيف صلاحية، بل يوسّع النطاق فقط. هذا مقصود لمنع تصعيد الصلاحيات.
  return result;
}

export function hasPermission(ctx: AccessContext, key: PermissionKey): boolean {
  return effectivePermissions(ctx).has(key);
}

export function hasAnyPermission(ctx: AccessContext, keys: PermissionKey[]): boolean {
  const effective = effectivePermissions(ctx);
  return keys.some((k) => effective.has(k));
}

/* =============================================================================
 * حل نطاق الوصول إلى مجموعة محددة
 * ========================================================================== */

export interface ResolvedScope {
  /** يرى كل مهام الشركة */
  companyWide: boolean;
  branchIds: Set<UUID>;
  brandIds: Set<UUID>;
  /** عضويات محددة أستطيع رؤية مهامها (نفسي + من أشرف عليه + منح مباشر) */
  membershipIds: Set<UUID>;
  /** تفاصيل قدرات الإشراف لكل عضوية مشرف عليها */
  supervisionCapabilities: Record<UUID, SupervisionCapability>;
}

export interface ScopeResolutionInput {
  ctx: AccessContext;
  companyId: UUID;
  /** كل الفروع والبراندات المستخدمة لتحويل نطاق 'brand/branch' بدون هدف محدد */
  allBranches: Array<{ id: UUID; brandId: UUID | null }>;
  allBrands: Array<{ id: UUID }>;
  brandBranchLinks: Array<{ brandId: UUID; branchId: UUID }>;
}

export function resolveScope(input: ScopeResolutionInput): ResolvedScope {
  const { ctx, companyId, allBranches, allBrands, brandBranchLinks } = input;
  const empty: ResolvedScope = {
    companyWide: false,
    branchIds: new Set(),
    brandIds: new Set(),
    membershipIds: new Set(),
    supervisionCapabilities: {},
  };

  const membership = ctx.membership;
  if (!membership || membership.membership.companyId !== companyId) {
    return ctx.isPlatformOwner ? { ...empty } : empty;
  }

  const self = membership.membership;
  const result: ResolvedScope = {
    companyWide: self.isCompanyOwner === true,
    branchIds: new Set(),
    brandIds: new Set(),
    membershipIds: new Set([self.id]),
    supervisionCapabilities: { ...membership.supervisionCapabilities },
  };

  for (const id of membership.supervisedMembershipIds) {
    result.membershipIds.add(id);
  }

  const scopeGrants: ScopeGrant[] = [];
  for (const role of membership.roles) {
    scopeGrants.push(...role.scopes);
  }
  for (const grant of membership.grants) {
    if (grant.revokedAt === null) {
      scopeGrants.push({ scopeType: grant.scopeType, targetId: grant.targetId });
    }
  }

  const brandIdsLinkedToBranches = (branchIds: Iterable<UUID>): UUID[] => {
    const set = new Set<UUID>();
    for (const branchId of branchIds) {
      for (const link of brandBranchLinks) {
        if (link.branchId === branchId) set.add(link.brandId);
      }
      const branch = allBranches.find((b) => b.id === branchId);
      if (branch?.brandId) set.add(branch.brandId);
    }
    return [...set];
  };

  /**
   * العلامات الممنوحة صراحةً فقط هي التي توسّع النطاق إلى كل فروعها.
   * العلامة المكتسبة من نطاق فرع تمنح رؤية مستوى العلامة، ولا يجوز أن
   * تفتح فروعًا شقيقة لأن ذلك يخرق عزل مستخدم الفرع.
   */
  const directBrandIds = new Set<UUID>();

  const tag =
    scopeGrants.length === 0
      ? [{ scopeType: 'self' as ScopeType, targetId: null }]
      : scopeGrants;

  for (const grant of tag) {
    switch (grant.scopeType) {
      case 'all_platform':
        // نطاق المنصة لا يُمنح عبر دور شركة. يُتجاهل هنا عمدًا.
        break;
      case 'company':
        result.companyWide = true;
        break;
      case 'brand': {
        if (grant.targetId) {
          result.brandIds.add(grant.targetId);
          directBrandIds.add(grant.targetId);
        } else {
          for (const b of allBrands) {
            result.brandIds.add(b.id);
            directBrandIds.add(b.id);
          }
        }
        break;
      }
      case 'branch': {
        if (grant.targetId) {
          result.branchIds.add(grant.targetId);
          for (const b of brandIdsLinkedToBranches([grant.targetId])) result.brandIds.add(b);
        } else {
          const primary = self.primaryBranchId;
          if (primary) {
            result.branchIds.add(primary);
            for (const b of brandIdsLinkedToBranches([primary])) result.brandIds.add(b);
          }
        }
        break;
      }
      case 'supervised':
        for (const id of membership.supervisedMembershipIds) result.membershipIds.add(id);
        break;
      case 'self':
      default:
        result.membershipIds.add(self.id);
        break;
    }
  }

  // الفروع المرتبطة بالعلامات الممنوحة صراحةً تُضاف تلقائيًا.
  if (directBrandIds.size > 0) {
    for (const branch of allBranches) {
      if (branch.brandId && directBrandIds.has(branch.brandId)) {
        result.branchIds.add(branch.id);
      }
    }
    for (const link of brandBranchLinks) {
      if (directBrandIds.has(link.brandId)) result.branchIds.add(link.branchId);
    }
  }

  return result;
}

/* =============================================================================
 * الوصول إلى المهام
 * ========================================================================== */

export interface TaskAccessSubject {
  task: Pick<
    Task,
    | 'id'
    | 'companyId'
    | 'branchId'
    | 'brandId'
    | 'isPersonal'
    | 'visibility'
    | 'createdByMembershipId'
    | 'approverMembershipId'
    | 'assigneeScope'
    | 'status'
    | 'approvalState'
    | 'requiresApproval'
  >;
  assigneeMembershipIds: UUID[];
  /** العضويات التي أنهت المهمة فعليًا */
  completerMembershipIds: UUID[];
}

export interface TaskAccessDecision {
  allowed: boolean;
  reason: string;
}

const DENY = (reason: string): TaskAccessDecision => ({ allowed: false, reason });

export function canViewTask(input: {
  ctx: AccessContext;
  scope: ResolvedScope;
  subject: TaskAccessSubject;
}): TaskAccessDecision {
  const { ctx, scope, subject } = input;
  const { task } = subject;
  const permissions = effectivePermissions(ctx);
  const membership = ctx.membership?.membership;

  if (!membership) {
    return ctx.isPlatformOwner && permissions.has('platform.audit.view')
      ? DENY('مالك المنصة لا يطّلع على تفاصيل مهام الشركات دون عضوية أو صلاحية صريحة')
      : DENY('لا توجد عضوية نشطة في هذه الشركة');
  }

  if (task.companyId !== membership.companyId) {
    return DENY('المهمة تنتمي إلى شركة أخرى');
  }

  const isCreator = task.createdByMembershipId === membership.id;
  const isAssignee = subject.assigneeMembershipIds.includes(membership.id);
  const isApprover = task.approverMembershipId === membership.id;

  // المهام الشخصية والخاصة لا يراها إلا طرف مباشر فيها.
  if (task.isPersonal && task.visibility === 'private') {
    if (isCreator || isAssignee || isApprover) return { allowed: true, reason: 'طرف في المهمة الشخصية' };
    return DENY('مهمة شخصية خاصة');
  }

  if (!permissions.has('task.view')) return DENY('لا تملك صلاحية عرض المهام');

  if (isCreator || isAssignee || isApprover) return { allowed: true, reason: 'طرف في المهمة' };
  if (permissions.has('task.review')) {
    if (scope.companyWide) return { allowed: true, reason: 'إدارة مهام الشركة' };
    if (task.branchId && scope.branchIds.has(task.branchId)) return { allowed: true, reason: 'إدارة مهام الفرع' };
    if (task.brandId && scope.brandIds.has(task.brandId)) return { allowed: true, reason: 'إدارة مهام البراند' };
  }
  const supervisedTaskParties = [
    task.createdByMembershipId,
    task.approverMembershipId,
    ...subject.assigneeMembershipIds,
  ];
  if (supervisedTaskParties.some((id) => id && scope.supervisionCapabilities[id]?.canViewTasks === true)) {
    return { allowed: true, reason: 'مشرف على مسؤول في المهمة' };
  }

  return DENY('لست مسؤولًا عن المهمة ولا مشرفًا على مسؤول فيها');
}

export function canEditTask(input: {
  ctx: AccessContext;
  scope: ResolvedScope;
  subject: TaskAccessSubject;
}): TaskAccessDecision {
  const view = canViewTask(input);
  if (!view.allowed) return view;
  const { ctx, subject } = input;
  const membership = ctx.membership?.membership;
  const permissions = effectivePermissions(ctx);
  const isCreator = subject.task.createdByMembershipId === membership?.id;

  if (isCreator && permissions.has('task.create')) return { allowed: true, reason: 'منشئ المهمة' };
  if (permissions.has('task.update') && input.scope.companyWide) {
    return { allowed: true, reason: 'صلاحية تعديل بنطاق الشركة' };
  }
  if (permissions.has('task.update') && subject.task.createdByMembershipId && isSupervisedMembership(subject.task.createdByMembershipId, input.scope)) {
    return { allowed: true, reason: 'تعديل مهام من تشرف عليهم' };
  }
  if (isCreator) return { allowed: true, reason: 'منشئ المهمة (تعديل محدود)' };
  return DENY('لا تملك صلاحية تعديل هذه المهمة');
}

export function canDeleteTask(input: {
  ctx: AccessContext;
  scope: ResolvedScope;
  subject: TaskAccessSubject;
}): TaskAccessDecision {
  const view = canViewTask(input);
  if (!view.allowed) return view;
  const { ctx, subject } = input;
  const membership = ctx.membership?.membership;
  const permissions = effectivePermissions(ctx);
  const isCreator = subject.task.createdByMembershipId === membership?.id;

  if (subject.task.isPersonal && isCreator) {
    return { allowed: true, reason: 'منشئ المهمة الشخصية' };
  }
  if (isCreator && (permissions.has('task.delete') || permissions.has('task.create'))) {
    return { allowed: true, reason: 'منشئ المهمة' };
  }
  if (permissions.has('task.delete')) {
    if (input.scope.companyWide) return { allowed: true, reason: 'صلاحية حذف بنطاق الشركة' };
    if (subject.task.branchId && input.scope.branchIds.has(subject.task.branchId)) {
      return { allowed: true, reason: 'صلاحية حذف بنطاق الفرع' };
    }
    if (subject.task.brandId && input.scope.brandIds.has(subject.task.brandId)) {
      return { allowed: true, reason: 'صلاحية حذف بنطاق البراند' };
    }
    if (
      subject.task.createdByMembershipId &&
      isSupervisedMembership(subject.task.createdByMembershipId, input.scope)
    ) {
      return { allowed: true, reason: 'حذف مهام من تشرف عليهم' };
    }
  }
  return DENY('لا تملك صلاحية حذف هذه المهمة');
}

function isSupervisedMembership(id: UUID, scope: ResolvedScope): boolean {
  return scope.membershipIds.has(id) && scope.supervisionCapabilities[id] !== undefined;
}
export function canCompleteTask(input: {
  ctx: AccessContext;
  subject: TaskAccessSubject;
}): TaskAccessDecision {
  const membership = input.ctx.membership?.membership;
  if (!membership) return DENY('لا توجد عضوية نشطة');
  if (!effectivePermissions(input.ctx).has('task.complete')) return DENY('لا تملك صلاحية إكمال المهام');
  if (input.subject.task.status === 'cancelled') return DENY('المهمة ملغاة');
  if (!input.subject.task.isPersonal && !input.subject.assigneeMembershipIds.includes(membership.id)) {
    return DENY('لست مسؤولًا عن هذه المهمة');
  }
  if (input.subject.task.isPersonal && input.subject.task.createdByMembershipId !== membership.id) {
    return DENY('هذه مهمة شخصية لغيرك');
  }
  return { allowed: true, reason: 'مسؤول عن المهمة' };
}

/**
 * اعتماد الإنجاز.
 * قواعد صريحة:
 *  1) من أكمل المهمة لا يعتمد إنجازه بنفسه.
 *  2) المعتمد المحدد في المهمة هو المرجع الأول.
 *  3) يجوز للمشرف على المعتمد الاعتماد نيابة عند تفويض canApproveTasks (تصعيد).
 *  4) يجوز لصاحب نطاق الشركة وصلاحية task.approve الاعتماد.
 */
export function canApproveTask(input: {
  ctx: AccessContext;
  scope: ResolvedScope;
  subject: TaskAccessSubject;
}): TaskAccessDecision {
  const { ctx, scope, subject } = input;
  const membership = ctx.membership?.membership;
  if (!membership) return DENY('لا توجد عضوية نشطة');
  if (!effectivePermissions(ctx).has('task.approve')) return DENY('لا تملك صلاحية الاعتماد');
  if (subject.task.requiresApproval === false) return DENY('هذه المهمة لا تتطلب اعتمادًا');
  if (subject.task.status === 'cancelled') return DENY('المهمة ملغاة');

  if (subject.completerMembershipIds.includes(membership.id)) {
    return DENY('لا يمكنك اعتماد إنجازك الشخصي');
  }

  if (subject.task.approverMembershipId === membership.id) {
    return { allowed: true, reason: 'المعتمد المحدد للمهمة' };
  }

  const capability = subject.task.approverMembershipId
    ? scope.supervisionCapabilities[subject.task.approverMembershipId]
    : undefined;
  if (capability?.canApproveTasks) {
    return { allowed: true, reason: 'الاعتماد نيابة عن معتمد تشرف عليه' };
  }

  if (scope.companyWide) return { allowed: true, reason: 'صلاحية اعتماد على مستوى الشركة' };

  if (subject.task.approverMembershipId && scope.membershipIds.has(subject.task.approverMembershipId)) {
    return { allowed: true, reason: 'المعتمد داخل نطاقك' };
  }

  return DENY('لست المعتمد لهذه المهمة');
}

export function canAssignTask(input: {
  ctx: AccessContext;
  scope: ResolvedScope;
  subject: TaskAccessSubject;
}): TaskAccessDecision {
  const view = canViewTask(input);
  if (!view.allowed) return view;
  if (!effectivePermissions(input.ctx).has('task.assign')) return DENY('لا تملك صلاحية الإسناد');
  if (input.subject.task.status === 'cancelled') return DENY('المهمة ملغاة');
  return { allowed: true, reason: 'صلاحية إسناد' };
}

/**
 * هل يمكن إسناد المهمة إلى هذه العضوية؟
 * تُستخدم لمنع تكليف شخص خارج نطاق يقبل التكليف منك.
 */
export function canAssignToMembership(input: {
  ctx: AccessContext;
  scope: ResolvedScope;
  targetMembership: { id: UUID; companyId: UUID };
}): TaskAccessDecision {
  const membership = input.ctx.membership?.membership;
  if (!membership) return DENY('لا توجد عضوية نشطة');
  if (!effectivePermissions(input.ctx).has('task.assign')) return DENY('لا تملك صلاحية الإسناد');
  if (input.targetMembership.companyId !== membership.companyId) {
    return DENY('لا يمكن إسناد مهمة لمستخدم من شركة أخرى');
  }
  if (input.targetMembership.id === membership.id) return { allowed: true, reason: 'إسناد لنفسك' };
  if (input.scope.companyWide) return { allowed: true, reason: 'نطاق الشركة' };
  if (input.scope.membershipIds.has(input.targetMembership.id)) {
    const capability = input.scope.supervisionCapabilities[input.targetMembership.id];
    if (capability && capability.canAssignTasks) return { allowed: true, reason: 'تكليف ضمن الإشراف' };
    return { allowed: true, reason: 'العضوية ضمن نطاقك' };
  }
  return DENY('العضوية خارج نطاق إسنادك');
}

/** تصحيح snapshot النطاق لأغراض العرض والتحقق فقط، ولا يمنح صلاحية. */
export function snapshotToScopeTargets(snapshot: AssigneeScopeSnapshot): {
  branchIds: UUID[];
  brandIds: UUID[];
  membershipIds: UUID[];
} {
  return {
    branchIds: [...snapshot.branchIds],
    brandIds: [...snapshot.brandIds],
    membershipIds: [...snapshot.membershipIds],
  };
}

export function pickTimeZone(company: { timeZone: TimeZone }): TimeZone {
  return company.timeZone || 'Asia/Riyadh';
}
/* =============================================================================
 * أدوات بناء السياق
 * ========================================================================== */

export interface BuildContextInput {
  userId: UUID;
  isPlatformOwner: boolean;
  platformPermissions?: PermissionKey[];
  memberships: Membership[];
  /** ربط العضوية بأدوارها: membershipId -> roleIds */
  membershipRoleMap: Record<UUID, UUID[]>;
  roles: Role[];
  rolePermissions: RolePermission[];
  roleScopes: RoleScope[];
  grants: MembershipAccessGrant[];
  supervision: Array<{
    supervisorMembershipId: UUID;
    subordinateMembershipId: UUID;
    canViewTasks: boolean;
    canAssignTasks: boolean;
    canApproveTasks: boolean;
    canViewReports: boolean;
    startsAt: string;
    endsAt: string | null;
  }>;
  companyId: UUID | null;
  now?: Date;
}

export function buildAccessContext(input: BuildContextInput): AccessContext {
  const now = input.now ?? new Date();
  const activeMemberships = input.memberships.filter(
    (m) => isMembershipActive(m) && (m.endedAt === null || new Date(m.endedAt) > now),
  );

  const roleById = new Map(input.roles.map((r) => [r.id, r]));
  const permissionsByRole = new Map<UUID, PermissionKey[]>();
  for (const rp of input.rolePermissions) {
    const list = permissionsByRole.get(rp.roleId) ?? [];
    if (isPermissionKey(rp.permissionKey)) list.push(rp.permissionKey);
    permissionsByRole.set(rp.roleId, list);
  }
  const scopesByRole = new Map<UUID, ScopeGrant[]>();
  for (const rs of input.roleScopes) {
    const list = scopesByRole.get(rs.roleId) ?? [];
    list.push({ scopeType: rs.scopeType, targetId: rs.targetId });
    scopesByRole.set(rs.roleId, list);
  }

  const activeSupervision = input.supervision.filter(
    (s) => s.startsAt && (!s.endsAt || new Date(s.endsAt) > now),
  );

  const contextFor = (membership: Membership): MembershipContext => {
    const roleGrants: RoleGrant[] = [];
    for (const mr of input.membershipRoleMap[membership.id] ?? []) {
      const role = roleById.get(mr);
      if (!role) continue;
      roleGrants.push({
        role: {
          id: role.id,
          name: role.name,
          key: role.key,
          priority: role.priority,
          isPlatformRole: role.isPlatformRole,
        },
        permissions: permissionsByRole.get(role.id) ?? [],
        scopes: scopesByRole.get(role.id) ?? [],
      });
    }
    roleGrants.sort((a, b) => a.role.priority - b.role.priority);

    const supervised = activeSupervision.filter(
      (s) => s.supervisorMembershipId === membership.id,
    );
    const capabilities: Record<UUID, SupervisionCapability> = {};
    for (const s of supervised) {
      capabilities[s.subordinateMembershipId] = {
        canViewTasks: s.canViewTasks,
        canAssignTasks: s.canAssignTasks,
        canApproveTasks: s.canApproveTasks,
        canViewReports: s.canViewReports,
      };
    }

    return {
      membership,
      roles: roleGrants,
      grants: input.grants.filter(
        (g) => g.membershipId === membership.id && g.revokedAt === null && g.companyId === membership.companyId,
      ),
      supervisedMembershipIds: supervised.map((s) => s.subordinateMembershipId),
      supervisionCapabilities: capabilities,
    };
  };

  const target = input.companyId
    ? activeMemberships.find((m) => m.companyId === input.companyId) ?? null
    : null;

  return {
    userId: input.userId,
    isPlatformOwner: input.isPlatformOwner,
    platformPermissions: input.platformPermissions ?? [],
    membership: target ? contextFor(target) : null,
    memberships: activeMemberships,
  };
}

/** نفّذ حل النطاق من سياق جاهز. */
export function buildContextAndScope(
  input: BuildContextInput,
  scopeInput: Omit<ScopeResolutionInput, 'ctx'>,
): { ctx: AccessContext; scope: ResolvedScope } {
  const ctx = buildAccessContext(input);
  return { ctx, scope: resolveScope({ ctx, ...scopeInput }) };
}
